import { useLingui } from '@lingui/react/macro';
import { Command } from 'cmdk';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { useEffect, useId, useState, type ReactNode, type UIEvent } from 'react';

import { cn } from './cn';
import { fieldControlClass } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Spinner } from './spinner';

export interface ComboboxOption {
  readonly value: string;
  readonly label: string;
  readonly description?: ReactNode;
  readonly disabled?: boolean;
}

export interface ComboboxProps {
  readonly options: readonly ComboboxOption[];
  readonly value: string | null | undefined;
  readonly onChange?: (value: string | null, option: ComboboxOption | null) => void;
  /**
   * Busca remota (contrato `AutocompleteOptionList` do legado). Com ela, o filtro local desliga e
   * a lista mostrada é a que o chamador devolve em `options`.
   */
  readonly onSearch?: (query: string) => void;
  /** Espera entre a última tecla e o `onSearch`. O legado usava 500 ms. */
  readonly searchDelayMs?: number;
  readonly loading?: boolean;
  /** Paginação por rolagem: chamado perto do fim da lista. */
  readonly onScrollEnd?: () => void;
  /** Rótulo do valor atual quando ele não está nas `options` (busca remota já trocou a lista). */
  readonly selectedLabel?: string;
  readonly placeholder?: string;
  readonly searchPlaceholder?: string;
  readonly emptyMessage?: ReactNode;
  readonly id?: string;
  readonly name?: string;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly required?: boolean;
  readonly className?: string;
  readonly 'aria-labelledby'?: string;
  readonly 'aria-describedby'?: string;
  readonly 'aria-invalid'?: boolean;
  readonly 'aria-required'?: boolean;
  readonly 'aria-label'?: string;
}

/** Escolha com busca (Popover + cmdk), local ou remota. Casa pelo valor, como o `Select`. */
export function Combobox({
  options,
  value,
  onChange,
  onSearch,
  searchDelayMs = 300,
  loading = false,
  onScrollEnd,
  selectedLabel,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  id,
  name,
  disabled,
  readOnly,
  required,
  className,
  ...aria
}: ComboboxProps) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const listId = useId();
  const [query, setQuery] = useState('');
  const [remembered, setRemembered] = useState<ComboboxOption | null>(null);

  useEffect(() => {
    if (!onSearch || !open) return;
    const timer = setTimeout(() => onSearch(query), searchDelayMs);
    return () => clearTimeout(timer);
  }, [onSearch, open, query, searchDelayMs]);

  const current =
    options.find((option) => option.value === value) ??
    (remembered?.value === value ? remembered : null);
  const label = current?.label ?? (value ? selectedLabel : undefined);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const list = event.currentTarget;
    if (onScrollEnd && list.scrollHeight - list.scrollTop - list.clientHeight < 48) onScrollEnd();
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          id={id}
          aria-expanded={open}
          aria-controls={listId}
          disabled={disabled === true || readOnly === true}
          data-slot="combobox-trigger"
          className={cn(fieldControlClass, 'justify-between gap-2 text-left', !label && 'text-muted-foreground', className)}
          {...aria}
        >
          <span className="truncate">{label ?? placeholder ?? t`Selecione`}</span>
          <ChevronsUpDown aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      {name ? <input type="hidden" name={name} value={value ?? ''} required={required} /> : null}
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) min-w-64 p-0">
        <Command shouldFilter={!onSearch} loop className="flex flex-col">
          <div className="flex items-center gap-2 border-b border-border px-3">
            <Search aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder={searchPlaceholder ?? t`Buscar…`}
              className="h-control w-full bg-transparent text-field outline-none placeholder:text-faint"
            />
            {loading ? <Spinner className="size-4 text-muted-foreground" /> : null}
          </div>
          <Command.List id={listId} className="max-h-72 overflow-y-auto p-1" onScroll={handleScroll}>
            {loading && options.length === 0 ? null : (
              <Command.Empty className="px-2 py-3 text-center text-sm text-muted-foreground">
                {emptyMessage ?? t`Nenhum resultado encontrado`}
              </Command.Empty>
            )}
            {options.map((option) => (
              <Command.Item
                key={option.value}
                value={option.value}
                keywords={[option.label]}
                disabled={option.disabled === true}
                onSelect={() => {
                  const cleared = option.value === value;
                  setRemembered(cleared ? null : option);
                  onChange?.(cleared ? null : option.value, cleared ? null : option);
                  setOpen(false);
                  setQuery('');
                }}
                className={cn(
                  'relative flex min-h-10 items-center gap-2 rounded-md py-1.5 pr-8 pl-2 text-control outline-none select-none md:min-h-8',
                  'transition-colors duration-100 data-[selected=true]:bg-accent-wash data-[selected=true]:text-accent-text aria-selected:font-medium data-[disabled=true]:opacity-45',
                )}
              >
                <div className="min-w-0">
                  <p className="truncate">{option.label}</p>
                  {option.description ? <p className="truncate text-xs text-muted-foreground">{option.description}</p> : null}
                </div>
                {option.value === value ? (
                  <Check aria-hidden className="absolute right-2 size-4 text-accent-text" />
                ) : null}
              </Command.Item>
            ))}
          </Command.List>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
